/**
 * 날짜 고르개가 공유하는 **순수 함수**들 (2026-09-01 · 채택 2단계 5순위).
 *
 * ★★ 여기에 `Date` 를 문자열로 바꾸는 일은 없다 — 화면에 보이는 글자는
 *    `lib/base-i18n` 의 `formatDate`/`getDateLocale` 이 만든다.
 *    로케일을 못 박으면 ja/en 사용자에게 틀린 표기가 나가고, 레포의 검사가 그것을 막는다.
 */

/** `YYYY-MM-DD` — 서버와 주고받는 값의 형태. **시각·시간대를 싣지 않는다.** */
export function toDayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * `YYYY-MM-DD` → 그 날의 **로컬 자정**.
 * ★ `new Date('2026-09-01')` 은 **UTC 자정**으로 읽혀 시간대에 따라 하루가 밀린다.
 *   한국(UTC+9)에서는 안 밀리지만, 그 사실에 기대면 시간대가 다른 곳에서 조용히 틀린다.
 */
export function fromDayKey(key: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isSameDay(a: Date, b: Date): boolean {
  return toDayKey(a) === toDayKey(b);
}

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** 달 이동 — **그 달 1일**로 정규화한다. 「보고 있는 달」을 옮길 때 쓴다. */
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/**
 * 달 이동 — **날짜(일)를 지킨다.** 키보드 PageUp/PageDown 이 쓰는 것이다.
 *
 * ★★ `addMonths` 를 여기 쓰면 **날짜를 잃고 1일로 간다** — 10일에서 PageDown 을 눌렀는데
 *    다음 달 1일로 뛴다(실제로 그렇게 짰다가 검사가 잡았다).
 * ★ 짧은 달로 갈 때는 **그 달 마지막 날로 붙인다** — 1월 31일에서 다음 달은 3월 3일이 아니라 2월 28일이다.
 */
export function shiftMonthKeepDay(d: Date, n: number): Date {
  const day = d.getDate();
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(day, lastDay));
}

/**
 * 그 달의 격자 42칸(6주 × 7일). **달마다 높이가 변하면 안 되므로 항상 42다**(정본 C1).
 * 5주로 끝나는 달에 5주만 그리면 달을 넘길 때 팝오버가 껑충 뛴다.
 *
 * @param weekStart 0=일요일 시작(지금 캘린더 화면과 같다) · 1=월요일 시작
 */
export function monthGrid(month: Date, weekStart: 0 | 1 = 0): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const shift = (first.getDay() - weekStart + 7) % 7;
  const gridStart = addDays(first, -shift);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/** 두 날짜를 **작은 것부터** 돌려준다 — 사용자가 끝을 먼저 누를 수 있다. */
export function orderPair(a: Date, b: Date): [Date, Date] {
  return a.getTime() <= b.getTime() ? [a, b] : [b, a];
}

export function isWithin(d: Date, min?: Date | null, max?: Date | null): boolean {
  const t = startOfDay(d).getTime();
  if (min && t < startOfDay(min).getTime()) return false;
  if (max && t > startOfDay(max).getTime()) return false;
  return true;
}

/** 시각 문자열 `HH:mm` → 분. 아니면 null. */
export function parseHm(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}

/** 분 → `HH:mm`. **24시간제 고정**(정본 C4) — 오전/오후는 근무 시각에서 잘못 읽히기 쉽다. */
export function toHm(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
